import { Fragment } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Divider,
  List,
  ListItemButton,
} from '@mui/material';
import { useItems } from '@scorenco/components';
import {
  ssrApolloService,
  RouteLink,
  useRouteInfo,
  Types,
} from '@scorenco/core';
import {
  NextAdArea,
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextPageWithLayout,
} from '@scorenco/next';

import { PageLayout, PageSeo, SportChip } from '@/components';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';
import { getSport, extractBase36Id } from '@/utils';

import {
  GetCompetitionsByLevelSeasonDocument,
  GetLevelAndSeasonsDocument,
  GetLevelInSeasonInfosDocument,
} from './_internal/Level.query.generated';

const extractUrlInfo = (query: Record<string, string | undefined>) => {
  const levelId = extractBase36Id(query.level);

  return { levelId };
};

const Level: NextPageWithLayout<{ sport: Types.Clubs_Sport }> = ({ sport }) => {
  const routeInfo = useRouteInfo();
  const urlInfo = extractUrlInfo(routeInfo.query);

  const sportSlug = routeInfo.query.sport;
  const seasonSlug = routeInfo.query.saison;

  const levelsInSeasonQuery = useQuery(GetLevelInSeasonInfosDocument, {
    variables: {
      levelId: urlInfo.levelId as number,
      seasonSlug: seasonSlug as string,
    },
    skip: !urlInfo.levelId || !seasonSlug,
    context: {
      role: 'anonymous',
    },
  });

  const levelInSeason =
    levelsInSeasonQuery.data
      ?.competitions_level_for_season_detail_by_level_and_season?.[0];

  const levelAndSeasonsQuery = useQuery(GetLevelAndSeasonsDocument, {
    variables: {
      id: urlInfo.levelId as number,
    },
    skip: !!seasonSlug,
    context: {
      role: 'anonymous',
    },
  });

  const level = levelAndSeasonsQuery.data?.competitions_level_by_pk;

  const competitionsQuery = useQuery(GetCompetitionsByLevelSeasonDocument, {
    variables: {
      lfsId: levelInSeason?.id as number,
    },
    skip: !levelInSeason,
    context: {
      role: 'anonymous',
    },
  });

  const competitions = useItems(
    competitionsQuery.data
      ?.competitions_competition_detail_by_level_for_season_id
  );

  return (
    <div>
      <PageSeo
        title={`${levelInSeason?.name || level?.name} - Score'n'co`}
        description={`${
          levelInSeason?.name || level?.name
        } sur Score'n'co 🔵🟢`}
        url={{
          routeKey: RouteKey.Sport,
          params: {
            sport: sportSlug || '',
            level: level?.slug + '-' + level?.id.toString(36) || '',
          },
        }}
      />

      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      <PageLayout
        image={sport?.image}
        breadcrumbs={[
          {
            label: sport?.name || '',
            href: {
              routeKey: RouteKey.Competitions,
              params: { sport: sportSlug },
            },
          },
          {
            label: levelInSeason?.league?.name || level?.league?.name || '',
            href: {
              routeKey: RouteKey.League,
              params: {
                sport: sportSlug,
                league: `${
                  levelInSeason?.league?.slug || level?.league?.slug
                }-${
                  levelInSeason?.league?.id.toString(36) ||
                  level?.league?.id.toString(36)
                }`,
              },
            },
          },
          {
            label: levelInSeason?.name || level?.name || '',
          },
        ]}
      >
        <Paper variant="bordered">
          <Stack
            direction="row"
            spacing={4}
            width="100%"
            alignItems="center"
            sx={{
              '@media screen and (max-width: 450px)': {
                flexDirection: 'column',
              },
            }}
          >
            <Box
              sx={{
                '@media screen and (max-width: 450px)': {
                  margin: '0 !important',
                  textAlign: 'center',
                },
              }}
            >
              <SportChip
                sport={sport?.slug}
                sx={{
                  mb: 2,
                }}
              />
              <Typography variant="h1" component="h1">
                {levelInSeason?.name || level?.name}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <NextAdArea
          akceloSlotId="Scorenco_PlayRVideo"
          akceloAdUnitClass="akcelo-adunit-15786"
          withGooglePublisherTag
        />

        {seasonSlug ? (
          <Box sx={{ marginTop: 3 }}>
            <Typography
              variant="h5"
              sx={{ textTransform: 'uppercase', fontStyle: 'unset', mb: 2 }}
            >
              Compétitions
            </Typography>

            <Paper
              variant="bordered"
              sx={{ padding: 0, alignItems: 'stretch' }}
            >
              <List disablePadding>
                {competitions.map((competition, idx) => {
                  return (
                    <Fragment key={competition?.id || idx}>
                      {idx !== 0 && <Divider />}
                      <ListItemButton
                        component={RouteLink}
                        href={{
                          routeKey: RouteKey.Competition,
                          params: {
                            sport: sportSlug,
                            competition: `${competition?.slug}-${competition?.id.toString(
                              36
                            )}`,
                          },
                        }}
                      >
                        {competition?.name}
                      </ListItemButton>
                    </Fragment>
                  );
                })}
              </List>
            </Paper>
          </Box>
        ) : (
          <Box sx={{ marginTop: 3 }}>
            <Typography
              variant="h5"
              sx={{ textTransform: 'uppercase', fontStyle: 'unset', mb: 2 }}
            >
              Saisons
            </Typography>

            <Paper
              variant="bordered"
              sx={{ padding: 0, alignItems: 'stretch' }}
            >
              <List disablePadding>
                {level?.seasons?.map((season, idx) => (
                  <Fragment key={season?.id || idx}>
                    {idx !== 0 && <Divider />}
                    <ListItemButton
                      component={RouteLink}
                      href={{
                        routeKey: RouteKey.Level,
                        params: {
                          sport: sportSlug,
                          level: `${level?.slug}-${level?.id.toString(36)}`,
                          saison: season?.season?.slug,
                        },
                      }}
                    >
                      {season.name}
                      {season?.season?.current
                        ? season?.season?.name && (
                            <Chip
                              sx={{ ml: 2 }}
                              label={
                                season?.season?.current &&
                                'Saison en cours - ' + season?.season?.name
                              }
                            />
                          )
                        : season?.season?.name && (
                            <Chip
                              sx={{ ml: 2 }}
                              label={
                                season?.season?.name &&
                                'Ancienne saison - ' + season?.season?.name
                              }
                            />
                          )}
                    </ListItemButton>
                  </Fragment>
                ))}
              </List>
            </Paper>
          </Box>
        )}
      </PageLayout>
    </div>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async ({ query }, previous) => {
    const { apolloClient, addApolloState } = ssrApolloService();

    const urlInfo = extractUrlInfo(
      query as any as { sport: string; level: string }
    );

    const sportSlug = query.sport;
    const seasonSlug = query.saison as string;
    const levelId = urlInfo?.levelId;

    if (!levelId || typeof sportSlug !== 'string') {
      return { notFound: true };
    }

    const levelQuery = await apolloClient.query({
      query: GetLevelAndSeasonsDocument,
      variables: {
        id: levelId as number,
      },
      context: {
        role: 'anonymous',
      },
    });

    if (!levelQuery.data?.competitions_level_by_pk) {
      return { notFound: true };
    }

    if (seasonSlug) {
      const levelInSeasonQuery = await apolloClient.query({
        query: GetLevelInSeasonInfosDocument,
        variables: {
          levelId: levelId as number,
          seasonSlug: seasonSlug as string,
        },
        context: {
          role: 'anonymous',
        },
      });

      const levelInSeason =
        levelInSeasonQuery.data
          ?.competitions_level_for_season_detail_by_level_and_season?.[0];

      if (!levelInSeason) {
        return { notFound: true };
      }

      // if (isConnected(req) && levelInSeason?.old_url) {
      //   return {
      //     redirect: {
      //       statusCode: 302,
      //       destination:
      //         process.env.NEXT_PUBLIC_OLD_WEB_URL +
      //         '/' +
      //         levelInSeason?.old_url,
      //     },
      //   };
      // }

      // Chargement des données pour le rendu côté serveur
      await apolloClient.query({
        query: GetCompetitionsByLevelSeasonDocument,
        variables: {
          lfsId: levelInSeason?.id as number,
        },
        context: {
          role: 'anonymous',
        },
      });
    }

    const sport = await getSport(sportSlug);

    return addApolloState({
      ...previous,
      props: {
        ...previous.props,
        sport,
      },
    });
  },
]);

Level.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default Level;
